import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { useState, useEffect } from "react";
import { Spinner, Card, Placeholder, Button } from "react-bootstrap";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import CaixaTable, { caixaTableColumns } from "components/tables/CaixaTable";
import { CaixaApiResponse, caixaBreadcrumbItems } from "data/caixa";
import { useEmpresas } from "providers/EmpresaProvider";
import qs from "qs";
import FilterTab, { FilterTabItem } from "components/common/FilterTab";
import { Filter } from "helpers/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import FiltroOffCanva from "./FiltroOffCanva";
import Filtro from "./filtro";
import { format } from "date-fns";

const fetchCaixas = async (
  selectedIds: number[],
  tabFilter: string | undefined,
  dataInicial: string | undefined,
  dataFinal: string | undefined,
  pageIndex: number = 1,
  pageSize: number = 10
) => {
  const response = await axios.get<CaixaApiResponse>("/api/caixa", {
    params: {
      empresasId: selectedIds,
      tabFilter,
      dataInicial,
      dataFinal,
      pageIndex,
      pageSize,
    },
    paramsSerializer: (params) => qs.stringify(params, { indices: false }),
  });
  return response.data;
};

const fetchCaixasResumo = async ({
  selectedIds,
  dataInicial,
  dataFinal,
}: {
  selectedIds: number[];
  dataInicial?: string;
  dataFinal?: string;
}) => {
  const { data } = await axios.get("/api/caixa/resumo", {
    params: {
      empresasId: selectedIds,
      dataInicial,
      dataFinal,
    },
    paramsSerializer: (params) => qs.stringify(params, { indices: false }),
  });
  return data;
};

const Caixas = () => {
  const { selectedIds } = useEmpresas();
  const [tabFilter, setTabFilter] = useState<string | undefined>();
  const [dataInicial, setDataInicial] = useState<string | undefined>();
  const [dataFinal, setDataFinal] = useState<string | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0, // React Table usa índice base 0, API usa índice base 1
    pageSize: 10,
  });

  // Detecta quando um filtro está aplicado
  useEffect(() => {
    setFiltroAtivo(!!(dataInicial || dataFinal));
  }, [dataInicial, dataFinal]);

  const dataQuery = useQuery<CaixaApiResponse>({
    queryKey: [
      "caixas",
      selectedIds,
      tabFilter,
      dataInicial,
      dataFinal,
      pagination.pageIndex,
      pagination.pageSize,
    ],
    queryFn: () =>
      fetchCaixas(
        selectedIds,
        tabFilter,
        dataInicial,
        dataFinal,
        pagination.pageIndex + 1, // Converte para índice base 1 para a API
        pagination.pageSize
      ),
    placeholderData: keepPreviousData,
  });

  const tabsQuery = useQuery<{ data: FilterTabItem[] }>({
    queryKey: ["caixasResumo", selectedIds, dataInicial, dataFinal],
    queryFn: () => fetchCaixasResumo({ selectedIds, dataInicial, dataFinal }),
    placeholderData: keepPreviousData,
  });

  const tabs =
    tabsQuery.data?.data?.map((t) => {
      return { ...t, onClick: () => setTabFilter(t.value) };
    }) ?? [];

  const handleLimparFiltros = () => {
    setDataInicial(undefined);
    setDataFinal(undefined);
    dataQuery.refetch();
    tabsQuery.refetch();
  };

  const table = useReactTable({
    data: dataQuery.data?.data ?? [],
    columns: caixaTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: dataQuery.data?.total
      ? Math.ceil(dataQuery.data.total / pagination.pageSize)
      : 1,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (dataQuery.isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Carregando...</span>
      </Spinner>
    );

  // Display error message if isSuccess is false
  if (dataQuery.data && !dataQuery.data.isSuccess) {
    return (
      <div className="alert alert-danger">
        Erro ao carregar caixas:{" "}
        {dataQuery.data.message || "Ocorreu um erro desconhecido."}
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb items={caixaBreadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Caixas</h2>
          <div className="d-flex align-items-center">
            {filtroAtivo && (
              <Filtro onClear={handleLimparFiltros}>
                {dataInicial
                  ? format(new Date(dataInicial), 'dd/MM/yyyy')
                  : "Início"}
                {" - "}
                {dataFinal ? format(new Date(dataFinal), 'dd/MM/yyyy') : "Fim"}
              </Filtro>
            )}
            <Button
              variant={filtroAtivo ? "primary" : "outline-primary"}
              onClick={() => setShowFilters(true)}
            >
              <FontAwesomeIcon icon={faFilter} className="me-2" />
              Filtros
            </Button>
          </div>
        </div>

        <FiltroOffCanva
          isShow={showFilters}
          onClose={() => setShowFilters(false)}
          setDataInicial={setDataInicial}
          setDataFinal={setDataFinal}
          dataInicial={dataInicial}
          dataFinal={dataFinal}
        />

        {tabsQuery.isLoading ? (
          <Placeholder animation="wave" as="div" className="mb-2">
            <Placeholder xs={3} />
          </Placeholder>
        ) : (
          <FilterTab tabItems={tabs} className="mb-2" />
        )}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                <CaixaTable />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default Caixas;
